import styles from "./style.module.scss";

import { useAppSelector } from "@/store/hooks";
import { HiddenableContainer } from "@components/layout/HiddenableContainer";
import { useEffect, useRef, useState } from "react";
import { Button, Col } from "reactstrap";
import LoadingScreen from "@components/content/LoadingScreen";
import { DocumentsEntryType } from "../DocumentStatusList";
import cx from "classnames";
import { ClientStatusRowData, InterestConfigType, normalizeClientStatusRow } from "@utils/DocumentStatus";
import moment from "moment";
import { formatMoneyValue } from "@utils/Currency";
import { CloseProviderInvoiceRecibePubSub, DocumentStatusListPubSub, OpenProviderInvoiceRecibePubSub } from "@utils/pubsubs/PubSub";
import { showAlert } from "@utils/Alert";
import { CurrencyDataType } from "@/api/CurrencyApi";
import { DynamicFormRef } from "@mavichpe/dynamic-forms";
import { toNumberFormat } from "@utils/Utils";
import ProviderStatusForm from "../ProviderStatusForm";
import ProviderRecibeOffCanvas from "@components/layout/Offcanvas/ProviderInvoiceRecibeOffCanvas";
import { ProviderRecibeDataType } from "@/api/ProviderRecibeApi";

type PayProviderInvoicesFormProps = {
  useToggle?: boolean,
  fieldTitle?: string
}

const PayProviderInvoicesForm = (props: PayProviderInvoicesFormProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [recibeTotal, setRecibeTotal] = useState<number>(0);
  const [recibeDate, setRecibeDate] = useState<string>();
  const [recibeCurrency, setRecibeCurrency] = useState<CurrencyDataType>();
  const [selectedDocuments, setSelectedDocuments] = useState<DocumentsEntryType[]>([]);
  const [interestExemptedDocuments, setInterestExemptedDocuments] = useState<string[]>([]);
  const suscriptionData = useAppSelector(state => state.suscription?.suscription)
  const formRef = useRef<DynamicFormRef>(null);

  useEffect(() => {
    const today = moment();
    setRecibeDate(today.format("YYYY-MM-DD"))
  }, [])

  useEffect(() => {
    if (recibeDate) {
      const interestConfig: InterestConfigType = {
        interestOnDue: Number(suscriptionData?.interest_on_due || 0),
        isInterestOnDue: suscriptionData?.is_interest_on_due || false,
        interestExemptedDocuments
      }
      normalizeData(interestConfig, recibeDate)
    }
  }, [selectedDocuments, recibeDate])

  const normalizeData = async (interestConfig: InterestConfigType, recibeDate: string) => {
    try {
      setIsLoading(true)

      const normalizedData: ClientStatusRowData[] = await Promise.all(selectedDocuments.map((r) => normalizeClientStatusRow(recibeDate, r, interestConfig)));
      const total = normalizedData.reduce((a, v) => {
        return a + v.saldo
      }, Number(0));
      setRecibeTotal(Number(toNumberFormat(total)))
      if (normalizedData.length > 0) {
        setRecibeCurrency(normalizedData[0].currency);
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onDocumentToogle = (documents: DocumentsEntryType[], interestExemptedDocuments: string[]) => {
    setSelectedDocuments(documents);
    setInterestExemptedDocuments(interestExemptedDocuments)
  }

  const triggerDocumentCancellation = () => {
    if (!recibeCurrency) {
      showAlert("danger", "No fue posible determinar la moneda para este recibo")
      return
    }
    if (selectedDocuments.length > 0 && recibeTotal > 0) {
      OpenProviderInvoiceRecibePubSub.publish({
        openOffCanvas: true,
        recibeTotal,
        recibeDate: `${recibeDate}`,
        selectedDocuments,
        recibeCurrency,
      })
    } else {
      showAlert("danger", "Debe de seleccionar al menos una factura para generar un recibo")
    }
  }

  const onRecibeCreated = (generatedRecibe: ProviderRecibeDataType) => {
    formRef.current?.triggerFormSubmit()
    CloseProviderInvoiceRecibePubSub.publish({})
    DocumentStatusListPubSub.publish({ action: "Clear" })
    resetRecibeData()
  }

  const resetRecibeData = () => {
    setSelectedDocuments([]);
    setInterestExemptedDocuments([])
  }

  return (
    <>
      <ProviderStatusForm
        ref={formRef}
        preventMultipleCurrency
        useToggle
        disableProjectedDate
        forceProviderSelect
        hideAdditionalActions
        filters={["pendientes", "vencidas"]}
        onDocumentToogle={onDocumentToogle}
        onShowResult={() => setShowResults(true)}
      >
        <Col className={cx([styles.pullRight, styles.stickyElement, styles.selectedInvoiceTotal])} xs="12" sm="5" md="4">
          <h3 className="mb-0 text-center">Total: <b>{formatMoneyValue(recibeTotal, recibeCurrency?.currency_id || 1)}</b></h3>
        </Col>
      </ProviderStatusForm>

      <HiddenableContainer show={showResults}>
        <Button size="lg" color="success" type="button" data-testid="generate-provider-recibe" onClick={() => triggerDocumentCancellation()}>
          Generar Recibo
        </Button>
      </HiddenableContainer>

      <ProviderRecibeOffCanvas
        suscriptionData={suscriptionData || {}}
        onRecibeCreated={onRecibeCreated} />
      <LoadingScreen isLoading={isLoading} />
    </>
  )
}

export default PayProviderInvoicesForm;